'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { HiChevronDown, HiMenu, HiX } from 'react-icons/hi'; // HeroIcons for icons
import { useRouter } from 'next/navigation'; // For route changes

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false); // For desktop dropdown
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false); // For mobile dropdown
  const router = useRouter();
  const [activeTab, setActiveTab] = useState('/');

  const serviceLinks = {
    'Web Development': '/services/web-development',
    'E-commerce Website Development': '/services/ecomm-web-development',
    'DevOps & Solution Architecture': '/services/devops-solution-architecture',
    'Data Analytics and Deep Learning APIs': '/services/data-analysis-deep-learning',
    'Custom API Development': '/services/custom-api-development',
    'Data Analysis and Testing': '/services/data-analysis-testing',
    'Tech Consulting and Problem-Solving': '/services/tech-consulting-problem-solving',
    'Payment System Integration': '/services/payment-system-integration',
  };

  // Initialize on component mount
  useEffect(() => {
    setActiveTab(window.location.pathname); // Set active tab on page load or route change
  }, [router]);

  const toggleMobileMenu = () => setMenuOpen(!menuOpen);

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setMobileDropdownOpen(false);
  };

  const handleLinkClick = (path) => {
    setActiveTab(path);
    closeMobileMenu();
  };

  const handleDropdownMouseEnter = () => setDropdownOpen(true);
  const handleDropdownMouseLeave = () => setDropdownOpen(false);

  const toggleMobileDropdown = () => setMobileDropdownOpen(!mobileDropdownOpen);

  return (
    <>
      <nav className="flex items-center justify-between p-4 shadow-lg bg-white w-full fixed top-0 z-50">
        {/* Logo Section */}
        <div className="flex items-center ml-6">
          <Link href="/" onClick={() => handleLinkClick('/')}>
            <img
              src="/Logo_Main.webp"
              alt="Data Pulse Technologies Logo"
              className="h-12 mr-4 cursor-pointer transition duration-300 transform hover:scale-110"
            />
          </Link>
        </div>

        {/* Desktop Navigation Links */}
        <div className="hidden lg:flex space-x-6 items-center">
          <Link
            href="/"
            className={`${activeTab === '/' ? 'text-[#5226EF]' : 'text-black'} hover:text-blue-500`}
            onClick={() => handleLinkClick('/')}
          >
            Home
          </Link>
          <Link
            href="/about"
            className={`${activeTab === '/about' ? 'text-[#5226EF]' : 'text-black'} hover:text-blue-500`}
            onClick={() => handleLinkClick('/about')}
          >
            About
          </Link>

          {/* Services Dropdown */}
          <div
            className="relative group"
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
          >
            <button className="flex items-center text-black text-lg focus:outline-none hover:text-blue-500">
              Services
              <HiChevronDown className="ml-1 w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            {dropdownOpen && (
              <ul className="absolute z-[9999] left-0 top-full min-w-max list-none overflow-hidden rounded-lg bg-white text-base shadow-lg transition-all duration-300 ease-in-out transform opacity-100 translate-y-0">
                {Object.entries(serviceLinks).map(([key, value], index) => (
                  <li key={index}>
                    <Link
                      href={value}
                      className="block w-full px-4 py-2 text-sm text-neutral-700 hover:bg-gray-100"
                      onClick={() => handleLinkClick(value)}
                    >
                      {key}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Link
            href="/portfolio"
            className={`${activeTab === '/portfolio' ? 'text-[#5226EF]' : 'text-black'} hover:text-blue-500`}
            onClick={() => handleLinkClick('/portfolio')}
          >
            Portfolio
          </Link>
          <Link
            href="/collaborators"
            className={`${activeTab === '/collaborators' ? 'text-[#5226EF]' : 'text-black'} hover:text-blue-500`}
            onClick={() => handleLinkClick('/collaborators')}
          >
            Clients & Collaborations
          </Link>
          <Link
            href="/blogs"
            className={`${activeTab === '/blogs' ? 'text-[#5226EF]' : 'text-black'} hover:text-blue-500`}
            onClick={() => handleLinkClick('/blogs')}
          >
            Blogs
          </Link>
          <Link
            href="/contact-us"
            className={`${activeTab === '/contact-us' ? 'text-[#5226EF]' : 'text-black'} hover:text-blue-500`}
            onClick={() => handleLinkClick('/contact-us')}
          >
            Contact Us
          </Link>
        </div>

        {/* Get A Quote Button */}
        <div className="hidden lg:flex mr-6">
          <Link
            href="/contact-us"
            className="bg-[#13F3DB] text-black px-6 py-2 rounded-full shadow-lg transition-all duration-300 ease-in-out transform hover:bg-gray-800 hover:text-white hover:scale-105"
          >
            Get a Quote
          </Link>
        </div>

        {/* Hamburger Menu for Mobile */}
        <button className="lg:hidden flex items-center" onClick={toggleMobileMenu}>
          {menuOpen ? (
            <HiX className="h-8 w-8 text-black transition duration-300 transform hover:scale-110" />
          ) : (
            <HiMenu className="h-8 w-8 text-black transition duration-300 transform hover:scale-110" />
          )}
        </button>

        {/* Mobile Navigation Menu */}
        <div
          className={`fixed top-0 left-0 w-full h-full bg-white transform ${
            menuOpen ? 'translate-x-0' : '-translate-x-full'
          } transition-transform duration-300 lg:hidden z-40`}
        >
          <div className="flex justify-between items-center p-4">
            <div>
              <Link href="/" onClick={() => handleLinkClick('/')}>
                <img
                  src="/Logo_Main.webp"
                  alt="Data Pulse Technologies Logo"
                  className="h-10 cursor-pointer transition duration-300 transform hover:scale-110"
                />
              </Link>
            </div>
            <button className="text-black text-2xl" onClick={toggleMobileMenu}>
              ✖
            </button>
          </div>

          <ul className="flex flex-col space-y-4 p-8">
            <li>
              <Link
                href="/"
                className={`${activeTab === '/' ? 'text-[#5226EF]' : 'text-black'} text-lg hover:text-blue-500 transition duration-300`}
                onClick={() => handleLinkClick('/')}
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                href="/about"
                className={`${activeTab === '/about' ? 'text-[#5226EF]' : 'text-black'} text-lg hover:text-blue-500 transition duration-300`}
                onClick={() => handleLinkClick('/about')}
              >
                About
              </Link>
            </li>

            {/* Mobile Services Dropdown */}
            <li>
              <div className="relative">
                <button
                  onClick={toggleMobileDropdown}
                  className="flex items-center text-lg text-black hover:text-blue-500 transition duration-300"
                >
                  Services <HiChevronDown
                    className={`ml-1 w-4 h-4 transition-transform duration-300 ${
                      mobileDropdownOpen ? 'rotate-180' : 'rotate-0'
                    }`}
                  />
                </button>
                <ul
                  className={`ml-6 mt-2 space-y-2 transition-all duration-300 ease-in-out ${
                    mobileDropdownOpen ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0 overflow-hidden'
                  }`}
                >
                  {Object.entries(serviceLinks).map(([key, value], index) => (
                    <li key={index}>
                      <Link
                        href={value}
                        className="text-black hover:bg-gray-100 transition duration-300"
                        onClick={() => handleLinkClick(value)}
                      >
                        {key}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </li>

            <li>
              <Link
                href="/portfolio"
                className={`${activeTab === '/portfolio' ? 'text-[#5226EF]' : 'text-black'} text-lg hover:text-blue-500 transition duration-300`}
                onClick={() => handleLinkClick('/portfolio')}
              >
                Portfolio
              </Link>
            </li>
            <li>
              <Link
                href="/collaborators"
                className={`${activeTab === '/collaborators' ? 'text-[#5226EF]' : 'text-black'} text-lg hover:text-blue-500 transition duration-300`}
                onClick={() => handleLinkClick('/collaborators')}
              >
                Clients & Collaborations
              </Link>
            </li>
            <li>
              <Link
                href="/blogs"
                className={`${activeTab === '/blogs' ? 'text-[#5226EF]' : 'text-black'} text-lg hover:text-blue-500 transition duration-300`}
                onClick={() => handleLinkClick('/blogs')}
              >
                Blogs
              </Link>
            </li>
            <li>
              <Link
                href="/contact-us"
                className={`${activeTab === '/contact-us' ? 'text-[#5226EF]' : 'text-black'} text-lg hover:text-blue-500 transition duration-300`}
                onClick={() => handleLinkClick('/contact-us')}
              >
                Contact Us
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
